import { useEffect, useState } from "react";
import { collection, doc, onSnapshot, orderBy, query } from "firebase/firestore";
import { paths, PLAN_FEATURE_GROUPS, PLAN_TIER_DEFAULTS, planIncludes, tierOf, type Plan, type Tenant } from "@clevis/shared-types";
import { Button, Card, CardTitle, cn } from "@clevis/shared-ui";
import { db } from "../../lib/firebase";
import { call } from "../../lib/callFunction";
import { useAuth } from "../../hooks/useAuth";
import { IncidentalsCard } from "../../components/RingSetup";

type PlanRow = Plan & { id: string };

export function BillingPage() {
  const { tenantId } = useAuth();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [plans, setPlans] = useState<PlanRow[]>([]);
  const [busyPlan, setBusyPlan] = useState<string | null>(null);
  const [message, setMessage] = useState("");

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const status = params.get("status");
    if (status === "success") setMessage("Subscription active — welcome aboard!");
    if (status === "canceled") setMessage("Checkout canceled. No changes made.");
  }, []);

  useEffect(() => {
    if (!tenantId) return;
    const subs = [
      onSnapshot(doc(db, paths.tenant(tenantId)), (snap) => setTenant((snap.data() as Tenant) ?? null)),
      onSnapshot(query(collection(db, paths.plans()), orderBy("order")), (snap) =>
        setPlans(
          snap.docs
            .map((d) => ({ id: d.id, ...(d.data() as Plan) }))
            .filter((p) => p.showOnPricingPage && !p.archivedAt),
        ),
      ),
    ];
    return () => subs.forEach((u) => u());
  }, [tenantId]);

  async function subscribe(planId: string) {
    if (!tenantId) return;
    setBusyPlan(planId);
    setMessage("");
    try {
      const res = await call<{ url: string }>(
        "createBillingCheckout",
        { tenantId, planId },
        { mutation: false },
      );
      window.location.href = res.url;
    } catch (err) {
      setMessage((err as Error).message);
      setBusyPlan(null);
    }
  }

  async function openPortal() {
    if (!tenantId) return;
    setBusyPlan("portal");
    setMessage("");
    try {
      const res = await call<{ url: string }>("createBillingPortalSession", { tenantId }, { mutation: false });
      window.location.href = res.url;
    } catch (err) {
      setMessage((err as Error).message);
      setBusyPlan(null);
    }
  }

  const currentPlanId = tenant?.planId ?? null;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold">Billing</h1>
        <Button variant="ghost" onClick={openPortal} busy={busyPlan === "portal"}>
          Manage billing & invoices
        </Button>
      </div>
      {message ? <p className="text-sm text-ink">{message}</p> : null}
      {tenant ? (
        <Card>
          <CardTitle>Current status</CardTitle>
          <p className="text-sm text-ink">
            {currentPlanId
              ? `Plan: ${plans.find((p) => p.id === currentPlanId)?.name ?? currentPlanId}`
              : "No plan yet."}{" "}
            <span className="ml-1 rounded-full bg-steel/15 px-2 py-0.5 text-xs">{tenant.billingStatus}</span>
          </p>
        </Card>
      ) : null}
      {tenantId ? <IncidentalsCard tenantId={tenantId} /> : null}

      <div className="grid gap-3 md:grid-cols-3">
        {[...plans].sort((a, b) => a.monthlyPriceCents - b.monthlyPriceCents).map((p, i, sorted) => {
          // Everything the plan includes, and what's new over the plan below it.
          const tier = tierOf(p.features);
          const prev = i > 0 ? sorted[i - 1]! : null;
          const prevIncl = prev ? new Set(planIncludes(prev.features, tierOf(prev.features))) : new Set();
          const extra = planIncludes(p.features, tier).filter((f) => !prevIncl.has(f));
          return (
          <Card key={p.id} className={cn("flex flex-col", p.isMostPopular && "border-brandorange")}>
            <div className="flex-1">
              {p.isMostPopular ? (
                <div className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-brandorange">
                  Most popular
                </div>
              ) : null}
              <div className="text-base font-bold text-ink">{p.name}</div>
              <div className="mb-2 text-2xl font-bold text-ink">
                ${(p.monthlyPriceCents / 100).toFixed(0)}
                <span className="text-sm font-normal text-steel">/mo</span>
              </div>
              <p className="mb-2 text-xs text-steel">{PLAN_TIER_DEFAULTS[tier].pitch}</p>
              <ul className="mb-2 space-y-1 text-xs text-ink">
                {p.limits.maxUserSeats ? <li className="font-semibold">Up to {p.limits.maxUserSeats} team members</li> : <li className="font-semibold">Unlimited team members</li>}
                {p.bullets.map((b, j) => (
                  <li key={j}>• {b}</li>
                ))}
              </ul>
              <div className="mb-3 text-xs">
                <div className="mb-1 font-semibold text-ink">{prev ? `Everything in ${prev.name}, plus:` : "Includes:"}</div>
                {PLAN_FEATURE_GROUPS.map((g) => {
                  const items = extra.filter((f) => f.group === g);
                  if (!items.length) return null;
                  return (
                    <div key={g} className="mb-1.5">
                      <div className="text-[10px] font-semibold uppercase tracking-wide text-steel">{g}</div>
                      <ul className="space-y-0.5 text-steel">
                        {items.map((f) => (
                          <li key={f.label} className="flex gap-1.5">
                            <span className="text-success">✓</span>
                            <span>
                              {f.label}
                              {f.soon ? <span className="text-steel/70"> (coming)</span> : null}
                            </span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </div>
            <Button
              onClick={() => void subscribe(p.id)}
              busy={busyPlan === p.id}
              variant={p.id === currentPlanId ? "secondary" : "primary"}
              disabled={p.id === currentPlanId && tenant?.billingStatus === "active"}
            >
              {p.id === currentPlanId && tenant?.billingStatus === "active" ? "Current plan" : "Choose plan"}
            </Button>
          </Card>
          );
        })}
        {plans.length === 0 ? (
          <p className="col-span-3 py-8 text-center text-sm text-steel">
            No public plans yet — they appear here once published in the master admin.
          </p>
        ) : null}
      </div>
      <p className="text-center text-[11px] text-steel">
        Payments are processed by Stripe. Test mode — use card 4242 4242 4242 4242.
      </p>
    </div>
  );
}
